/**
 * 文档同步器服务
 * 负责确保文档ID和内容状态的同步变更
 *
 * 重要: 在使用此服务前，请先阅读文档ID生命周期指南:
 * @see ../DOCUMENT_ID_LIFECYCLE.md
 *
 * 核心功能：
 * 1. 提供原子性同步操作
 * 2. 协调ID变更和内容状态更新
 * 3. 维护数据一致性
 */
import { DocumentIdentitySource, } from '@readsavor/types';
import { DocumentIdentityService } from './DocumentIdentityService.js';
/**
 * 文档同步器服务类
 * 确保文档ID激活和内容状态更新的同步一致性
 */
export class DocumentSynchronizer {
    constructor() {
        // 私有观察者列表：(documentId, contentType, contentState) => void
        this.contentUpdateListeners = [];
    }
    /**
     * 获取单例实例
     */
    static getInstance() {
        if (!DocumentSynchronizer.instance) {
            DocumentSynchronizer.instance = new DocumentSynchronizer();
        }
        return DocumentSynchronizer.instance;
    }
    /**
     * 同步激活文档并设置内容状态
     * 作为原子操作执行，确保一致性
     *
     * @param identity 文档标识
     * @param contentState 内容状态
     * @param source 文档来源
     * @returns 操作是否成功
     */
    syncDocumentActivation(identity, contentState, source = DocumentIdentitySource.STORAGE) {
        // 获取服务实例
        const identityService = DocumentIdentityService.getInstance();
        // 验证文档ID
        if (!identityService.validateIdentity(identity)) {
            return false;
        }
        // 激活文档ID，并构造元数据
        const metadataForActivation = {};
        // 从 contentState 中安全地提取元数据字段
        // 假设这些字段如果存在于 contentState 中，则是期望的元数据
        if ('language' in contentState && typeof contentState.language === 'string') {
            metadataForActivation.language = contentState.language;
        }
        if ('targetLanguage' in contentState && typeof contentState.targetLanguage === 'string') {
            metadataForActivation.targetLanguage = contentState.targetLanguage;
        }
        // 你可以为其他期望从 contentState 传递到 DocumentMetadata 的字段添加类似的检查
        // 例如: if ('title' in contentState && typeof contentState.title === 'string') { metadataForActivation.title = contentState.title; }
        const isActivated = identityService.activateDocument(identity, source, metadataForActivation);
        // 如果激活失败，终止整个操作
        if (!isActivated) {
            return false;
        }
        // 发布内容状态更新事件
        this.notifyContentUpdate(identity.id, identity.type, contentState);
        return true;
    }
    // 实现
    syncDocumentDeactivation(identity) {
        const identityService = DocumentIdentityService.getInstance();
        const previousActiveIdentity = identityService.state.currentIdentity;
        const previousContentType = previousActiveIdentity ? previousActiveIdentity.type : null;
        let isDeactivatedSuccess = false;
        if (identity) {
            isDeactivatedSuccess = identityService.deactivateDocument(identity);
        }
        else {
            isDeactivatedSuccess = identityService.deactivateDocument();
        }
        if (!isDeactivatedSuccess) {
            return false;
        }
        this.notifyContentReset(previousContentType);
        return true;
    }
    /**
     * 订阅内容更新事件
     *
     * @param listener 监听器函数
     * @returns 取消订阅的函数
     */
    subscribeToContentUpdates(listener) {
        this.contentUpdateListeners.push(listener);
        // 返回取消订阅函数
        return () => {
            this.contentUpdateListeners = this.contentUpdateListeners.filter(l => l !== listener);
        };
    }
    /**
     * 通知内容更新
     *
     * @param docId 文档ID
     * @param contentType 文档类型
     * @param state 内容状态
     */
    notifyContentUpdate(docId, contentType, state) {
        this.contentUpdateListeners.forEach(listener => {
            try {
                listener(docId, contentType, state);
            }
            catch (error) {
                // No need to log here as per the new code block
            }
        });
    }
    /**
     * 通知内容重置
     * 发送特殊ID值作为信号，表示需要重置
     * @param previousContentType 先前激活的文档类型
     */
    notifyContentReset(previousContentType) {
        // 使用特殊值-1表示重置所有内容
        this.contentUpdateListeners.forEach(listener => {
            try {
                // 对于 reset，docId 为 -1，contentType 为之前激活的类型，state 为空对象
                listener(-1, previousContentType, {});
            }
            catch (error) {
                // No need to log here as per the new code block
            }
        });
    }
}
DocumentSynchronizer.instance = null;
/**
 * 创建文档同步器的工厂函数
 * @returns 文档同步器实例
 */
export function createDocumentSynchronizer() {
    return DocumentSynchronizer.getInstance();
}
//# sourceMappingURL=DocumentSynchronizer.js.map